import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Image,
  Badge,
  Spinner,
  useToast,
  SimpleGrid,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  Checkbox
} from "@chakra-ui/react";
import { FaSync, FaDownload, FaBoxOpen, FaList, FaChevronLeft, FaChevronRight, FaSearch, FaMagic } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import Card from "../Card/Card";
import CardHeader from "../Card/CardHeader";
import CardBody from "../Card/CardBody";

interface CatalogImage {
  link: string;
  variant?: string;
  height?: number;
  width?: number;
}

interface AmazonRelationship {
    type: string; 
    parentAsins?: string[];
}

interface AmazonCatalogData {
  images?: Array<{ variant?: string; link: string; height?: number; width?: number }>;
  relationships?: AmazonRelationship[];
}

interface AmazonProduct {
  asin: string;
  realAsin?: string;
  parentAsin?: string;
  title: string;
  sku: string;
  price?: number;
  imageUrl?: string | null;
  catalogImages?: CatalogImage[];
  catalogData?: AmazonCatalogData;
  variations?: AmazonProduct[];
  marketplaceId?: string;
  status?: string;
  condition?: string;
}

// Structure de réponse de l'API avec groupByParent=true
interface InventoryResponse {
  grouped: Array<{
    parentAsin: string;
    variants: AmazonProduct[];
  }>;
  orphans: AmazonProduct[];
  items?: AmazonProduct[]; // Fallback
}

interface DisplayItem {
  type: 'parent' | 'orphan';
  id: string; // parentAsin ou realAsin/asin
  title: string;
  image: string;
  sku?: string;
  variantCount?: number;
  variants?: AmazonProduct[]; 
  productData?: AmazonProduct; 
}

interface AIGroup {
  id: string;
  name: string;
  description?: string;
  asins: string[];
}

interface AIGroupsResponse {
  groups: AIGroup[];
  orphans: string[];
  analyzedAt: string | null;
}

const ITEMS_PER_PAGE = 12;
const POLL_INTERVAL_MS = 2500;

const AmazonInventory = () => {
  const navigate = useNavigate();
  const { currentUser, getToken } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  
  // Data States
  const [displayItems, setDisplayItems] = useState<DisplayItem[]>([]);
  const [asinToProduct, setAsinToProduct] = useState<Map<string, AmazonProduct>>(new Map());
  const [loading, setLoading] = useState(false);
  const [importingAsins, setImportingAsins] = useState<string[]>([]);
  
  // AI Groups (Gemini regrouping)
  const [aiGroupsData, setAiGroupsData] = useState<AIGroupsResponse | null>(null);
  const [analyzeStatus, setAnalyzeStatus] = useState<'idle' | 'analyzing' | 'completed' | 'failed'>('idle');
  const [viewMode, setViewMode] = useState<'default' | 'ai'>('ai');
  
  // Search State
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);

  // Modal States
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleModalClose = () => {
    setSelectedVariantAsins(new Set());
    setVariantSearchQuery("");
    lastVariantClickIndexRef.current = null;
    onClose();
  };
  const [selectedParent, setSelectedParent] = useState<DisplayItem | null>(null);
  const [selectedVariantAsins, setSelectedVariantAsins] = useState<Set<string>>(new Set());
  const [variantSearchQuery, setVariantSearchQuery] = useState("");
  const lastVariantClickIndexRef = useRef<number | null>(null);

  const autoAnalyzeTriggeredRef = useRef(false);

  useEffect(() => {
    fetchInventory();
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) fetchGroups();
  }, [currentUser]);

  // Auto-lancer l'analyse des groupes si pas encore fait et qu'on a de l'inventaire
  useEffect(() => {
    if (
      !autoAnalyzeTriggeredRef.current &&
      aiGroupsData &&
      (!aiGroupsData.groups || aiGroupsData.groups.length === 0) &&
      displayItems.length > 0 &&
      analyzeStatus === 'idle'
    ) {
      autoAnalyzeTriggeredRef.current = true;
      handleAnalyzeGroups();
    }
  }, [aiGroupsData, displayItems.length, analyzeStatus]);

  // Reset pagination when search query or items change
  useEffect(() => {
    setCurrentPage(1);
  }, [displayItems.length, searchQuery]);

  // --- HELPERS D'EXTRACTION DE DONNÉES ---

  const getProductImage = (product: AmazonProduct) => {
    if (product.imageUrl && typeof product.imageUrl === "string") return product.imageUrl;
    if (product.catalogImages?.length) {
      const main = product.catalogImages.find(img => img.variant === "MAIN")
        || product.catalogImages.sort((a, b) => (b.height ?? 0) - (a.height ?? 0))[0];
      if (main?.link) return main.link;
    }
    // Fallback: catalogData.images (format brut Amazon - flat ou nested)
    const raw = product.catalogData?.images as Array<{ link?: string; variant?: string; height?: number; width?: number; images?: Array<{ link?: string }> }> | undefined;
    if (Array.isArray(raw) && raw.length > 0) {
      const flat: Array<{ link?: string; variant?: string; height?: number; width?: number }> = [];
      for (const entry of raw) {
        if (entry?.images && Array.isArray(entry.images)) {
          flat.push(...(entry.images as Array<{ link?: string; variant?: string; height?: number; width?: number }>));
        } else if (entry?.link) {
          flat.push(entry);
        }
      }
      const main = flat.find((img: { variant?: string }) => img.variant === "MAIN")
        || flat.sort((a, b) => (b.height ?? 0) - (a.height ?? 0))[0];
      if (main?.link) return main.link;
    }
    return "https://via.placeholder.com/100";
  };

  const getProductAsin = (product: AmazonProduct) => {
      return product.realAsin || product.asin;
  };

  // C'est ici que la magie opère pour trouver le parent caché
  const getTrueParentAsin = (product: AmazonProduct): string | null => {
      // 1. Si le champ explicite existe et n'est pas lui-même
      if (product.parentAsin && product.parentAsin !== product.asin) {
          return product.parentAsin;
      }
      
      // 2. Chercher dans les relations du catalogue (SP-API structure)
      if (product.catalogData?.relationships) {
          // On cherche une relation de type "VARIATION" qui pointe vers un parent
          const variationRel = product.catalogData.relationships.find(r => 
              (r.type === 'VARIATION' || r.type === 'variation') && 
              r.parentAsins && 
              r.parentAsins.length > 0
          );
          
          if (variationRel && variationRel.parentAsins) {
              return variationRel.parentAsins[0];
          }
      }

      return null;
  };

  const cleanParentTitle = (title: string) => {
      // Nettoyage basique
      return title;
  };

  const addToAsinMap = (map: Map<string, AmazonProduct>, product: AmazonProduct) => {
    const asin = getProductAsin(product);
    if (!map.has(asin)) map.set(asin, product);
  };

  // --- MAIN FETCH LOGIC ---

  const fetchGroups = async () => {
    try {
      const token = await getToken();
      const res = await fetch("/.netlify/functions/amazonInventoryGroups", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data: AIGroupsResponse = await res.json();
        setAiGroupsData(data);
      }
    } catch (e) {
      console.warn("Could not fetch AI groups", e);
    }
  };

  const handleAnalyzeGroups = async () => {
    try {
      setAnalyzeStatus('analyzing');
      const token = await getToken();
      const res = await fetch("/.netlify/functions/amazonInventoryAnalyzeGroups", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ marketplace: "fr" }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast({ title: data?.error || t('aiGroupAnalysisFailed'), status: "error" });
        setAnalyzeStatus('failed');
        return;
      }
      const jobId = data.jobId;
      if (!jobId) {
        toast({ title: t('aiGroupAnalysisFailed'), status: "error" });
        setAnalyzeStatus('failed');
        return;
      }
      const poll = async () => {
        const pollRes = await fetch(
          `/.netlify/functions/checkTaskStatus?taskId=${encodeURIComponent(jobId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const pollData = await pollRes.json();
        if (pollData.status === 'completed') {
          await fetchGroups();
          setAnalyzeStatus('completed');
          setViewMode('ai');
          toast({ title: t('analyzeCompleted'), status: "success" });
        } else if (pollData.status === 'failed') {
          toast({ title: pollData.error || t('aiGroupAnalysisFailed'), status: "error" });
          setAnalyzeStatus('failed');
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      };
      setTimeout(poll, POLL_INTERVAL_MS);
    } catch (e) {
      console.error("Analyze groups error", e);
      toast({ title: t('aiGroupAnalysisFailed'), status: "error" });
      setAnalyzeStatus('failed');
    }
  };

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch("/.netlify/functions/amazonGetInventory?groupByParent=true", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data: InventoryResponse = await response.json();
        const items: DisplayItem[] = [];
        const asinMap = new Map<string, AmazonProduct>();

        // 1. Déjà groupés par l'API (Facile)
        if (data.grouped) {
          data.grouped.forEach(group => {
            if (!group.variants || group.variants.length === 0) return;
            group.variants.forEach(p => addToAsinMap(asinMap, p));
            const firstChild = group.variants[0];
            items.push({
              type: 'parent',
              id: group.parentAsin,
              title: cleanParentTitle(firstChild.title),
              image: getProductImage(firstChild),
              variantCount: group.variants.length,
              variants: group.variants,
              sku: `${group.variants.length} variations`
            });
          });
        }

        // 2. Orphelins (Le gros du travail de regroupement client)
        if (data.orphans) {
          const orphansMap = new Map<string, AmazonProduct[]>(); // Map<ParentID, Products[]>
          const trueOrphans: AmazonProduct[] = [];

          data.orphans.forEach(orphan => {
            addToAsinMap(asinMap, orphan);
            const detectedParentId = getTrueParentAsin(orphan);

            if (detectedParentId) {
                // On a trouvé un parent ! On groupe.
                if (!orphansMap.has(detectedParentId)) {
                    orphansMap.set(detectedParentId, []);
                }
                orphansMap.get(detectedParentId)?.push(orphan);
            } else {
                // Vraiment tout seul
                trueOrphans.push(orphan);
            }
          });

          // Transformation des groupes reconstitués en cartes "Parent"
          orphansMap.forEach((variants, parentAsin) => {
             // S'il y a plus d'un variant, on crée un dossier.
             // Même s'il y a un seul variant mais qu'il a un parentId, 
             // on pourrait vouloir l'afficher comme "Enfant de X", mais pour l'UX, 
             // s'il est seul dans la liste, autant l'afficher comme un produit importable direct.
             // -> CHOIX UX : On groupe seulement si > 1 item pour éviter les dossiers à 1 item.
             
             if (variants.length > 1) {
                const firstChild = variants[0];
                items.push({
                    type: 'parent',
                    id: parentAsin,
                    title: cleanParentTitle(firstChild.title),
                    image: getProductImage(firstChild),
                    variantCount: variants.length,
                    variants: variants,
                    sku: `${variants.length} variations`
                });
             } else {
                // Seul de sa famille dans cette liste -> on le traite comme un orphelin
                trueOrphans.push(variants[0]);
             }
          });

          // Ajout des vrais orphelins (et des enfants uniques)
          trueOrphans.forEach(orphan => {
            items.push({
              type: 'orphan',
              id: getProductAsin(orphan),
              title: orphan.title,
              image: getProductImage(orphan),
              sku: orphan.sku,
              productData: orphan
            });
          });
        }

        // 3. Fallback
        if (!data.grouped && !data.orphans && data.items) {
           data.items.forEach(item => {
             addToAsinMap(asinMap, item);
             items.push({
                type: 'orphan',
                id: getProductAsin(item),
                title: item.title,
                image: getProductImage(item),
                sku: item.sku,
                productData: item
             });
           });
        }
        
        setAsinToProduct(asinMap);
        setDisplayItems(items);
      } else {
        setDisplayItems([]);
      }
    } catch (error) {
      console.error("Failed to fetch inventory", error);
      toast({ title: t('error'), status: "error" });
    } finally {
      setLoading(false);
    }
  };

  // --- ACTIONS ---

  const handleOpenVariants = (item: DisplayItem) => {
    setSelectedParent(item);
    setSelectedVariantAsins(new Set());
    onOpen();
  };

  const toggleVariantSelection = (asin: string) => {
    setSelectedVariantAsins(prev => {
      const next = new Set(prev);
      if (next.has(asin)) next.delete(asin);
      else next.add(asin);
      return next;
    });
  };

  const handleVariantClick = (
    asin: string,
    index: number,
    filteredVariants: AmazonProduct[],
    e: React.MouseEvent
  ) => {
    if (e.shiftKey) {
      const anchor = lastVariantClickIndexRef.current ?? index;
      const start = Math.min(anchor, index);
      const end = Math.max(anchor, index);
      const rangeAsins = filteredVariants.slice(start, end + 1).map(v => getProductAsin(v));
      setSelectedVariantAsins(prev => {
        const next = new Set(prev);
        rangeAsins.forEach(a => next.add(a));
        return next;
      });
    } else {
      toggleVariantSelection(asin);
    }
    lastVariantClickIndexRef.current = index;
  };

  const filteredModalVariants = (() => {
    const list = selectedParent?.variants ?? [];
    const q = variantSearchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      v =>
        (v.title || "").toLowerCase().includes(q) ||
        (v.sku || "").toLowerCase().includes(q) ||
        (getProductAsin(v) || "").toLowerCase().includes(q)
    );
  })();

  const selectAllVariants = () => {
    if (filteredModalVariants.length === 0) return;
    setSelectedVariantAsins(new Set(filteredModalVariants.map(v => getProductAsin(v))));
  };

  const deselectAllVariants = () => {
    setSelectedVariantAsins(new Set());
  };

  const handleImportSelection = async () => {
    if (selectedVariantAsins.size === 0) return;
    const variants = selectedParent?.variants?.filter(v => selectedVariantAsins.has(getProductAsin(v))) ?? [];
    if (variants.length === 0) return;
    const primary = variants[0];
    const primaryAsin = getProductAsin(primary);
    setImportingAsins(prev => [...prev, primaryAsin]);
    const otherAsins = variants.slice(1).map(v => getProductAsin(v));

    try {
      const token = await getToken();

      const payload = {
        type: "amazon_import",
        userId: currentUser?.uid,
        asin: primaryAsin,
        name: selectedParent?.title || primary.title || `Import ${primaryAsin}`,
        marketplace: "fr",
        amazonData: {
          title: selectedParent?.title || primary.title,
          image: getProductImage(primary),
          sku: primary.sku,
          ...(primary.price != null && { price: primary.price }),
          ...(otherAsins.length > 0 && { variations: otherAsins }),
        },
      };

      const response = await fetch("/.netlify/functions/createProject", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: t('importStarted'),
          description: t('importSelectionAsOne', { count: variants.length }),
          status: "success",
          duration: 5000,
        });
        handleModalClose();
        if (data?.projectId) {
          navigate(`/projects/${data.projectId}`, { state: { fromAmazonImport: true } });
        }
        setSelectedVariantAsins(new Set());
      } else {
        throw new Error("Import failed");
      }
    } catch (error) {
      toast({ title: t('importError'), status: "error" });
    } finally {
      setImportingAsins(prev => prev.filter(id => id !== primaryAsin));
    }
  };

  const handleImport = async (product: AmazonProduct, siblingVariants?: AmazonProduct[]) => {
    try {
      const targetAsin = getProductAsin(product);
      setImportingAsins(prev => [...prev, targetAsin]);
      const token = await getToken();
      
      const payload = {
        type: "amazon_import",
        userId: currentUser?.uid,
        asin: targetAsin,
        name: product.title || `Import ${targetAsin}`,
        marketplace: "fr",
        amazonData: {
          title: product.title,
          image: getProductImage(product),
          sku: product.sku,
          ...(product.price != null && { price: product.price }),
          ...(siblingVariants && siblingVariants.length > 0 && {
            variations: siblingVariants.map(v => getProductAsin(v)),
          }),
        },
      };

      const response = await fetch("/.netlify/functions/createProject", {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: t('importStarted'),
          description: t('importStartedDesc'),
          status: "success",
          duration: 5000,
        });
        if (isOpen) handleModalClose();
        if (data?.projectId) {
          navigate(`/projects/${data.projectId}`, { state: { fromAmazonImport: true } });
        }
      } else {
        throw new Error("Import failed");
      }
    } catch (error) {
      toast({ title: t('importError'), status: "error" });
    } finally {
      setImportingAsins(prev => prev.filter(id => id !== getProductAsin(product)));
    }
  };

  // --- BUILD EFFECTIVE DISPLAY LIST (default vs AI groups) ---

  const effectiveDisplayItems = ((): DisplayItem[] => {
    if (viewMode !== 'ai' || !aiGroupsData?.groups?.length || asinToProduct.size === 0) {
      return displayItems;
    }
    const items: DisplayItem[] = [];
    for (const g of aiGroupsData.groups) {
      const variants = g.asins
        .map(as => asinToProduct.get(as))
        .filter((p): p is AmazonProduct => !!p);
      if (variants.length === 0) continue;
      const first = variants[0];
      if (variants.length === 1) {
        items.push({
          type: 'orphan',
          id: getProductAsin(first),
          title: first.title,
          image: getProductImage(first),
          sku: first.sku,
          productData: first,
        });
      } else {
        items.push({
          type: 'parent',
          id: g.id,
          title: g.name || first.title,
          image: getProductImage(first),
          variantCount: variants.length,
          variants,
          sku: `${variants.length} variations`,
        });
      }
    }
    for (const asin of aiGroupsData.orphans || []) {
      const p = asinToProduct.get(asin);
      if (p) {
        items.push({
          type: 'orphan',
          id: getProductAsin(p),
          title: p.title,
          image: getProductImage(p),
          sku: p.sku,
          productData: p,
        });
      }
    }
    return items;
  })();

  // --- FILTER & RENDER ---
  
  // Filtering logic
  const filteredItems = effectiveDisplayItems.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
        item.title.toLowerCase().includes(query) ||
        item.id.toLowerCase().includes(query) ||
        (item.sku && item.sku.toLowerCase().includes(query))
    );
  });

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const currentItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const InventoryCard = ({ item }: { item: DisplayItem }) => (
    <Box 
      bg="linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)"
      borderRadius="20px" p={5} border="1px solid" borderColor="gray.700" transition="all 0.3s ease"
      _hover={{ transform: "translateY(-5px)", borderColor: "teal.400", boxShadow: "0 10px 20px -10px rgba(0, 128, 128, 0.4)" }}
      display="flex" flexDirection="column" h="100%"
    >
      <HStack align="start" spacing={4} mb={4}>
        <Image src={item.image} boxSize="80px" objectFit="contain" bg="white" borderRadius="12px" p={1} fallbackSrc="https://via.placeholder.com/80" />
        <VStack align="start" flex={1} spacing={1}>
            <HStack wrap="wrap">
                <Badge colorScheme={item.type === 'parent' ? "purple" : "orange"} borderRadius="full" px={2} fontSize="0.6em">
                    {item.type === 'parent' ? t('parent') : t('product')}
                </Badge>
                {item.type === 'parent' && (
                     <Badge colorScheme="gray" borderRadius="full" px={2} fontSize="0.6em">
                        {t('variations', { count: item.variantCount })}
                     </Badge>
                )}
            </HStack>
            <Text fontWeight="bold" noOfLines={2} color="white" fontSize="sm" lineHeight="shorter">{item.title}</Text>
            <Text fontSize="xs" color="gray.500">{item.id}</Text>
        </VStack>
      </HStack>
      
      <Box mt="auto">
        {item.type === 'parent' ? (
            <Button 
                w="100%" size="sm" colorScheme="purple" leftIcon={<FaList />}
                onClick={() => handleOpenVariants(item)}
                borderRadius="10px"
            >
                {t('seeVariations')}
            </Button>
        ) : (
            <Button 
                w="100%" size="sm" colorScheme="teal" leftIcon={<FaDownload />}
                onClick={() => item.productData && handleImport(item.productData)}
                isLoading={importingAsins.includes(item.id)}
                loadingText={t('importStarted')}
                borderRadius="10px"
            >
                {t('importProject')}
            </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Card p="20px" my={{ sm: "24px", xl: "0px" }}>
      <CardHeader>
        <Box w="100%">
            {/* Top Row: Title + Refresh */}
            <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
                <HStack flexWrap="wrap">
                    <Text fontSize="lg" color="white" fontWeight="bold">{t('amazonInventory')}</Text>
                    <Badge colorScheme="teal" borderRadius="12px" px={2}>
                        {filteredItems.length} {t('items')}
                    </Badge>
                    {aiGroupsData?.analyzedAt && (
                      <Text fontSize="xs" color="gray.400">
                        {t('groupsAnalyzedAt', { date: new Date(aiGroupsData.analyzedAt).toLocaleString() })}
                      </Text>
                    )}
                </HStack>
                <HStack>
                    {aiGroupsData?.groups?.length ? (
                      <Button
                        size="sm"
                        variant={viewMode === 'ai' ? 'solid' : 'outline'}
                        colorScheme="purple"
                        onClick={() => setViewMode(viewMode === 'ai' ? 'default' : 'ai')}
                      >
                        {viewMode === 'ai' ? t('viewByDefault') : t('viewByAIGroups')}
                      </Button>
                    ) : null}
                    <Button 
                        size="sm" leftIcon={<FaMagic />}
                        onClick={handleAnalyzeGroups}
                        isLoading={analyzeStatus === 'analyzing'}
                        loadingText={t('analyzingGroups')}
                        colorScheme="purple"
                        variant="ghost"
                    >
                        {t('analyzeProductGroups')}
                    </Button>
                    <Button 
                        size="sm" leftIcon={<FaSync />} onClick={fetchInventory} 
                        isLoading={loading} colorScheme="teal" variant="ghost"
                    >
                        {t('refresh')}
                    </Button>
                </HStack>
            </HStack>

            {/* Search Bar */}
            <InputGroup size="md" maxW="400px">
                <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input 
                    placeholder={t('searchProduct')} 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg="gray.800"
                    border="1px solid"
                    borderColor="gray.700"
                    color="white"
                    _placeholder={{ color: "gray.500" }}
                    _focus={{ borderColor: "teal.400" }}
                    borderRadius="15px"
                />
            </InputGroup>
        </Box>
      </CardHeader>
      
      <CardBody>
        {analyzeStatus === 'analyzing' ? (
            <Box py={16} textAlign="center" w="100%" bg="gray.800" borderRadius="20px" px={8}>
                <Spinner size="xl" color="purple.400" thickness="4px" mb={6} />
                <Text color="white" fontSize="lg" fontWeight="bold" mb={2}>
                    {t('buildingAutomaticGroups')}
                </Text>
                <Text color="gray.400" fontSize="sm" maxW="md" mx="auto" mb={6}>
                    {t('buildingAutomaticGroupsDesc')}
                </Text>
                <Progress size="xs" isIndeterminate colorScheme="purple" maxW="300px" mx="auto" borderRadius="full" />
            </Box>
        ) : loading && displayItems.length === 0 ? (
            <Box py={20} textAlign="center" w="100%">
                <Spinner size="xl" color="teal.400" thickness="4px" />
                <Text mt={4} color="gray.400" fontWeight="medium">{t('loadingInventory')}</Text>
            </Box>
        ) : displayItems.length === 0 ? (
            <Box py={20} textAlign="center" w="100%" bg="gray.800" borderRadius="20px">
                <Icon as={FaBoxOpen} boxSize={16} color="gray.600" mb={4} />
                <Text color="white" fontSize="lg" fontWeight="bold" mb={2}>{t('noProductsFound')}</Text>
                <Text fontSize="sm" color="gray.400" maxW="md" mx="auto">
                    {t('checkConnection')}
                </Text>
            </Box>
        ) : (
            <VStack spacing={6} w="100%">
                {/* Check if filtered results are empty */}
                {filteredItems.length === 0 && searchQuery ? (
                     <Box py={10} textAlign="center" w="100%">
                        <Text color="gray.400">{t('noResultsFoundFor')} "{searchQuery}"</Text>
                    </Box>
                ) : (
                    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="24px" w="100%">
                        {currentItems.map((item) => (
                            <InventoryCard key={item.id} item={item} />
                        ))}
                    </SimpleGrid>
                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <HStack spacing={4} justify="center" w="100%" pt={4}>
                        <IconButton
                            aria-label="Previous page"
                            icon={<FaChevronLeft />}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            isDisabled={currentPage === 1}
                            size="sm" variant="ghost" colorScheme="whiteAlpha"
                        />
                        <Text color="white" fontSize="sm">
                            Page {currentPage} / {totalPages}
                        </Text>
                        <IconButton
                            aria-label="Next page"
                            icon={<FaChevronRight />}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            isDisabled={currentPage === totalPages}
                            size="sm" variant="ghost" colorScheme="whiteAlpha"
                        />
                    </HStack>
                )}
            </VStack>
        )}
      </CardBody>

      {/* Variations Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg="gray.900" borderColor="gray.700" borderWidth="1px" color="white" maxW="80vw" maxH="80vh">
            <ModalHeader>
                {selectedParent?.title}
                <Text fontSize="sm" fontWeight="normal" color="gray.400" mt={1}>
                    {t('selectVariationsToImport')}
                </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
                <InputGroup mb={4}>
                    <InputLeftElement pointerEvents="none">
                        <Icon as={FaSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder={t('searchVariants')}
                        value={variantSearchQuery}
                        onChange={e => setVariantSearchQuery(e.target.value)}
                        bg="gray.800"
                        borderColor="gray.700"
                        _placeholder={{ color: "gray.500" }}
                    />
                </InputGroup>
                <HStack mb={4} spacing={3}>
                    <Button size="xs" variant="ghost" colorScheme="teal" onClick={selectAllVariants}>
                        {t('selectAll')}
                    </Button>
                    <Button size="xs" variant="ghost" colorScheme="gray" onClick={deselectAllVariants}>
                        {t('deselectAll')}
                    </Button>
                    {selectedVariantAsins.size > 0 && (
                        <Badge colorScheme="teal" borderRadius="full" px={2}>
                            {selectedVariantAsins.size} {t('selected')}
                        </Badge>
                    )}
                    {variantSearchQuery && (
                        <Text fontSize="xs" color="gray.500">
                            {filteredModalVariants.length} / {selectedParent?.variants?.length ?? 0}
                        </Text>
                    )}
                    <Text fontSize="xs" color="gray.500" ml="auto">
                        {t('variantSelectionHint')}
                    </Text>
                </HStack>
                <VStack spacing={3} align="stretch">
                    {filteredModalVariants.map((variant, idx) => {
                        const asin = getProductAsin(variant);
                        return (
                            <Box
                                key={asin}
                                p={3} borderRadius="12px" bg="gray.800"
                                display="flex" alignItems="center" justifyContent="space-between"
                                borderWidth="1px" borderColor={selectedVariantAsins.has(asin) ? "teal.500" : "gray.700"}
                                _hover={{ borderColor: "teal.500" }}
                                cursor="pointer"
                                onClick={e => handleVariantClick(asin, idx, filteredModalVariants, e)}
                            >
                                <HStack spacing={3} flex={1} overflow="hidden">
                                    <Checkbox
                                        isChecked={selectedVariantAsins.has(asin)}
                                        onChange={() => {
                                            toggleVariantSelection(asin);
                                            lastVariantClickIndexRef.current = idx;
                                        }}
                                        colorScheme="teal"
                                        onClick={e => e.stopPropagation()}
                                    />
                                    <Image
                                        src={getProductImage(variant)}
                                        boxSize="50px" objectFit="contain" bg="white" borderRadius="8px" p={1}
                                        fallbackSrc="https://via.placeholder.com/50"
                                    />
                                    <VStack align="start" spacing={0} flex={1} minW={0}>
                                        <Text fontSize="sm" fontWeight="bold" noOfLines={2} w="100%">
                                            {variant.title}
                                        </Text>
                                        <HStack>
                                            <Badge fontSize="0.6em" colorScheme="blue">{variant.sku}</Badge>
                                            <Text fontSize="xs" color="gray.500">{asin}</Text>
                                        </HStack>
                                    </VStack>
                                </HStack>
                            </Box>
                        );
                    })}
                </VStack>
            </ModalBody>
            <ModalFooter>
                <Button variant="ghost" mr={3} onClick={handleModalClose}>
                    {t('close')}
                </Button>
                <Button
                    colorScheme="teal"
                    onClick={handleImportSelection}
                    isDisabled={selectedVariantAsins.size === 0}
                    isLoading={importingAsins.some(id => selectedParent?.variants?.some(v => getProductAsin(v) === id))}
                    loadingText={t('importStarted')}
                >
                    {t('importSelection')} {selectedVariantAsins.size > 0 ? `(${selectedVariantAsins.size})` : ''}
                </Button>
            </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default AmazonInventory;